// StudentDashboard.jsx - Main student interface that shows courses and navigation
import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { UserContext } from "../Context";
import JoinCourseModal from "../components/JoinCourseModal";
import { FiGrid, FiPlus, FiUser, FiChevronDown } from "react-icons/fi";
import { getApiUrl } from "../utils/api";
import "../styles/studentDashboard.css";

const StudentDashboard = () => {
  const { user, logout } = useContext(UserContext);
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [showCourseDropdown, setShowCourseDropdown] = useState(false);
  const [showJoinModal, setShowJoinModal] = useState(false);

  useEffect(() => {
    if (user) {
      fetchCourses();
    }
  }, [user]);

  const fetchCourses = async () => {
    try {
      const response = await fetch(getApiUrl("/api/course/get"), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });

      const data = await response.json();

      // Handle both new format {status: "success", courses: [...]} and old format {courses: [...]}
      let courseList = [];
      if (data.status === "success" && data.courses) {
        courseList = data.courses;
      } else if (data.courses) {
        // Old format compatibility
        courseList = data.courses;
      }

      setCourses(courseList);

      // Auto-select first course if available and none selected
      if (courseList.length > 0 && !selectedCourse) {
        setSelectedCourse(courseList[0]);
        // Note: Removed auto-redirect logic - students should be routed appropriately from login
      }
    } catch (error) {
      console.error("Error fetching courses:", error);
    }
  };

  const handleCourseSelect = (course) => {
    setSelectedCourse(course);
    setShowCourseDropdown(false);
    navigate(`/sessions/${course.course_number}/${course.course_name}`);
  };

  const handleJoinCourse = () => {
    setShowJoinModal(true);
  };

  const onCourseJoined = async (courseData) => {
    setShowJoinModal(false);
    await fetchCourses(); // Refresh course list
    
    // Navigate to the newly joined course's chat page
    if (courseData && courseData.course_number && courseData.course_name) {
      const navigateUrl = `/sessions/${courseData.course_number}/${courseData.course_name}`;
      navigate(navigateUrl);
    } else {
      console.error("❌ Missing course data for navigation:", courseData);
    }
  };

  // If student has no courses, show join course interface
  if (courses.length === 0) {
    return (
      <div className="student-dashboard no-courses">
        <div className="dashboard-header">
          <div className="dashboard-logo">
            <img src="/merryquery-logo.png" alt="MerryQuery" />
            <span>MerryQuery</span>
          </div>
          <div className="user-profile">
            <div className="user-avatar">
              <FiUser />
            </div>
            <div className="user-details">
              <span className="user-name">{user?.full_name}</span>
              <div className="user-dropdown">
                <button onClick={logout}>Log out</button>
              </div>
            </div>
          </div>
        </div>

        <div className="welcome-content">
          <div className="welcome-message">
            <h1>Welcome to MerryQuery!</h1>
            <p>
              To get started, please join a course using your instructor's
              6-digit course code.
            </p>
            <button className="join-course-btn" onClick={handleJoinCourse}>
              <FiPlus />
              Join a Course
            </button>
          </div>
        </div>

        {showJoinModal && (
          <JoinCourseModal
            isVisible={showJoinModal}
            onClose={() => setShowJoinModal(false)}
            onSuccess={onCourseJoined}
          />
        )}
      </div>
    );
  }

  // Normal dashboard view when courses exist
  return (
    <div className="student-dashboard">
      <div className="dashboard-header">
        <div
          className="course-selector"
          onClick={() => setShowCourseDropdown(!showCourseDropdown)}
        >
          <FiGrid />
          <span>{selectedCourse?.course_name || "Select Course"}</span>
          <FiChevronDown />

          {showCourseDropdown && (
            <div className="course-dropdown">
              <div className="dropdown-header">Your Courses</div>
              {courses.map((course) => (
                <div
                  key={course._id}
                  className={`dropdown-item ${
                    selectedCourse?._id === course._id ? "active" : ""
                  }`}
                  onClick={() => handleCourseSelect(course)}
                >
                  {course.course_name}
                </div>
              ))}
              <div className="dropdown-divider" />
              <div
                className="dropdown-item join-new"
                onClick={handleJoinCourse}
              >
                <FiPlus />
                Join New Course
              </div>
            </div>
          )}
        </div>

        <div className="user-profile">
          <div className="user-avatar">
            <FiUser />
          </div>
          <div className="user-details">
            <span className="user-name">{user?.full_name}</span>
          </div>
        </div>
      </div>

      {showJoinModal && (
        <JoinCourseModal
          isVisible={showJoinModal}
          onClose={() => setShowJoinModal(false)}
          onSuccess={onCourseJoined}
        />
      )}
    </div>
  );
};

export default StudentDashboard;
